/**
 * 扩展的任务模型
 * 基于 AISD 方法论，增强任务与规范、设计、工作流、AI 的关联
 */

import { Task, TaskStatus, TaskPriority } from './index';

/**
 * Agent 分配信息
 */
export interface AgentAssignment {
  /** 主要执行的 Agent ID */
  primary: string;
  /** 审查的 Agent ID 列表 */
  reviewers?: string[];
  /** 咨询的 Agent ID 列表 */
  consultants?: string[];
  /** 分配时间 */
  assignedAt: string;
  /** 分配原因 */
  reason?: string;
}

/**
 * Prompt 模板配置
 */
export interface PromptTemplateConfig {
  /** 执行时使用的 Prompt 模板 ID */
  execution?: string;
  /** 验证时使用的 Prompt 模板 ID */
  validation?: string;
  /** 审查时使用的 Prompt 模板 ID */
  review?: string;
  /** 自定义 Prompt 模板 */
  custom?: Record<string, string>;
}

/**
 * 任务产出物
 */
export interface TaskArtifacts {
  /** 代码相关产出 */
  code?: {
    /** 生成或修改的代码文件 */
    files: string[];
    /** 相关的 Git commits */
    commits?: string[];
    /** 代码行数统计 */
    stats?: {
      added: number;
      modified: number;
      deleted: number;
    };
  };
  /** 文档相关产出 */
  docs?: {
    /** 生成或更新的文档文件 */
    files: string[];
    /** 生成的图表文件 */
    diagrams?: string[];
    /** 文档类型 */
    types?: ('api' | 'guide' | 'spec' | 'readme')[];
  };
  /** 测试相关产出 */
  tests?: {
    /** 测试文件 */
    files: string[];
    /** 测试覆盖率 */
    coverage?: number;
    /** 测试结果 */
    results?: {
      total: number;
      passed: number;
      failed: number;
      skipped: number;
    };
  };
  /** 其他产出 */
  other?: {
    type: string;
    description: string;
    files: string[];
  }[];
}

/**
 * 扩展的任务接口
 */
export interface ExtendedTask extends Task {
  // ========== 规范关联 ==========
  /** 关联的需求规范 ID (requirements.md) */
  specId?: string;
  /** 关联的设计文档 ID (design.md) */
  designId?: string;
  /** 关联的工作流定义 ID */
  workflowId?: string;
  /** 关联的 PRD ID */
  prdId?: string;

  // ========== AI 协作 ==========
  /** Agent 分配信息 */
  agentAssignments?: AgentAssignment;
  /** Prompt 模板配置 */
  promptTemplates?: PromptTemplateConfig;
  /** AI 执行模式 */
  aiMode?: 'autopilot' | 'supervised' | 'collaborative' | 'manual';
  /** AI 执行历史 */
  aiExecutions?: Array<{
    agentId: string;
    timestamp: string;
    action: string;
    result: 'success' | 'failure' | 'partial';
    duration: number;
    notes?: string;
  }>;

  // ========== 任务产出 ==========
  /** 任务产出物 */
  artifacts?: TaskArtifacts;
  
  // ========== 验收标准 ==========
  /** 验收标准（EARS 格式） */
  acceptanceCriteria?: Array<{
    id: string;
    given: string;
    when: string;
    then: string;
    and?: string[];
    verified: boolean;
    verifiedAt?: string;
    verifiedBy?: string;
  }>;

  // ========== 增强的元数据 ==========
  metadata?: {
    /** 任务来源 */
    source?: 'manual' | 'prd' | 'ai-suggestion' | 'workflow' | 'hook';
    /** 任务类型细分 */
    subtype?: string;
    /** 技术栈标签 */
    techStack?: string[];
    /** 影响范围 */
    scope?: 'component' | 'module' | 'system' | 'global';
    /** 风险等级 */
    riskLevel?: 'low' | 'medium' | 'high' | 'critical';
    /** 自定义字段 */
    custom?: Record<string, any>;
  };

  // ========== 协作信息 ==========
  /** 参与者 */
  participants?: Array<{
    id: string;
    role: 'owner' | 'assignee' | 'reviewer' | 'watcher';
    name: string;
    avatar?: string;
    joinedAt: string;
  }>;

  // ========== 工作流状态 ==========
  /** 工作流执行状态 */
  workflowStatus?: {
    /** 当前步骤 */
    currentStep?: string;
    /** 已完成步骤 */
    completedSteps?: string[];
    /** 下一步骤 */
    nextSteps?: string[];
    /** 执行进度 (0-100) */
    progress?: number;
  };
}

/**
 * 任务类型守卫
 */
export function isExtendedTask(task: Task | ExtendedTask): task is ExtendedTask {
  return 'specId' in task || 'agentAssignments' in task || 'artifacts' in task;
}

/**
 * 将普通任务转换为扩展任务
 */
export function toExtendedTask(task: Task): ExtendedTask {
  return {
    ...task,
    metadata: task.metadata || {}
  };
}

/**
 * 任务关联类型
 */
export type TaskRelationType = 
  | 'spec'      // 规范关联
  | 'design'    // 设计关联
  | 'workflow'  // 工作流关联
  | 'prd'       // PRD 关联
  | 'parent'    // 父任务
  | 'subtask'   // 子任务
  | 'dependency' // 依赖
  | 'blocks';    // 阻塞

/**
 * 任务关联信息
 */
export interface TaskRelation {
  /** 关联类型 */
  type: TaskRelationType;
  /** 源任务 ID */
  sourceId: string;
  /** 目标任务 ID */
  targetId: string;
  /** 关联描述 */
  description?: string;
  /** 创建时间 */
  createdAt: string;
}

/**
 * 任务执行上下文
 */
export interface TaskExecutionContext {
  /** 任务信息 */
  task: ExtendedTask;
  /** 相关规范 */
  specifications?: Array<{
    id: string;
    type: 'requirement' | 'design' | 'test';
    content: string;
  }>;
  /** 依赖任务状态 */
  dependencies?: Array<{
    taskId: string;
    status: TaskStatus;
    completed: boolean;
  }>;
  /** 可用的 Agent */
  availableAgents?: string[];
  /** 可用的 Prompt 模板 */
  availablePrompts?: string[];
  /** 项目上下文 */
  projectContext?: {
    language?: string;
    framework?: string;
    techStack?: string[];
    conventions?: Record<string, any>;
  };
}